// Navigation target for a group's landing surface. TanStack Router types
// the `to` field against the route tree, so we build the call shape (path +
// params) rather than concatenating a string — the latter compiles but
// silently no-ops at runtime when the literal isn't a registered route.

// Matches the server's `groups_slug_format_check` (GROUP_SLUG_RE in
// apps/server/src/groups/routes/_shared/group-ref.ts — server code can't be
// imported here, so the pattern is mirrored). Lowercase UUIDs are slug-shaped
// too, so consumers must test isUuid FIRST when the distinction matters.
export const GROUP_SLUG_RE = /^[a-z0-9][a-z0-9-]{0,62}[a-z0-9]$/;

type FeedComposeSearch = { tab: 'feed'; compose: true };

export type GroupNavigateArgs =
  | { to: '/circles/$id'; params: { id: string }; search?: FeedComposeSearch }
  | { to: '/networks/$id'; params: { id: string }; search?: FeedComposeSearch }
  | { to: '/feed'; search: { scope: string } };

export type GroupKind = 'circle' | 'network' | 'space';

export function navigateArgsForGroup(
  kind: GroupKind,
  id: string,
  slug?: string | null,
  // `compose` lands on the group's Feed tab with its composer opened — the
  // group analogue of /feed?compose=1, used by the join-link onboarding
  // "Say hello" step so a new member posts in the group they just joined.
  opts?: { compose?: boolean },
): GroupNavigateArgs {
  // Spaces aren't yet a first-class detail URL in the web app; the
  // canonical landing for now is the feed scoped to that group. Circles
  // and networks have dedicated detail routes.
  //
  // The detail GETs accept slug or uuid, so the pretty slug (when known) is
  // the canonical address-bar param. The feed scope is consumed by uuid-only
  // endpoints and always carries the uuid.
  const composeSearch =
    opts?.compose === true
      ? { search: { tab: 'feed', compose: true } as const }
      : null;
  switch (kind) {
    case 'circle':
      return {
        to: '/circles/$id',
        params: { id: slug ?? id },
        ...(composeSearch ?? {}),
      };
    case 'network':
      return {
        to: '/networks/$id',
        params: { id: slug ?? id },
        ...(composeSearch ?? {}),
      };
    case 'space':
      return { to: '/feed', search: { scope: `group:${id}` } };
  }
}
